import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, Loader2, Trash2, Smartphone } from 'lucide-react';
import { api } from '../lib/api';
import { RhythmInput, addDays } from './RhythmDatesPicker';

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const inp = { width: '100%', fontFamily: 'Lexend', fontSize: 12.5, background: 'transparent', border: 'none', color: 'var(--t1)', outline: 'none' };

export default function PostageWizard({ Modal, Field, users, onClose, onSaved, onBack, defaultClient = '' }) {
  const [docText, setDocText] = useState('');
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [client, setClient] = useState(defaultClient);
  const [posterId, setPosterId] = useState('');
  const [posts, setPosts] = useState(null);
  const [startDate, setStartDate] = useState(addDays(todayIso(), 1));
  const [rhythm, setRhythm] = useState(null);
  const [saving, setSaving] = useState(false);
  const posters = useMemo(() => (users || []).filter((u) => !u.is_archived && u.role !== 'client')
    .sort((a, b) => (b.job_role === 'posteur') - (a.job_role === 'posteur')), [users]);

  const analyse = async () => {
    if (docText.trim().length < 20) { toast.error('Colle le contenu du doc (trop court)'); return; }
    setLoading(true);
    try {
      const r = await api.post('/projects/postage/preview', { text: docText.trim() });
      setName(r.data.name || ''); if (!client) setClient(r.data.client || '');
      setPosts(r.data.posts || []);
      toast.success(`${(r.data.posts || []).length} post(s) détecté(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur IA'); }
    finally { setLoading(false); }
  };

  const applyRhythm = () => {
    if (!rhythm || !startDate) { toast.error('Choisis une date de début et un rythme'); return; }
    setPosts((ps) => ps.map((p, i) => ({ ...p, date: addDays(startDate, Math.floor(i / rhythm.per_slot) * rhythm.every_days) })));
    toast.success(`Dates appliquées · ${rhythm.label}`);
  };
  const upd = (i, k, v) => setPosts((ps) => ps.map((p, j) => (j === i ? { ...p, [k]: v } : p)));

  const submit = async () => {
    if (!name.trim()) { toast.error('Donne un nom au projet'); return; }
    if (!posterId) { toast.error('Choisis le posteur'); return; }
    const clean = posts.filter((p) => p.title.trim());
    if (!clean.length) { toast.error('Aucun post'); return; }
    setSaving(true);
    try {
      const r = await api.post('/projects/postage', { name: name.trim(), client: client.trim(), poster_id: posterId, posts: clean });
      toast.success(`Projet Postage créé · ${r.data.count} post(s) envoyés « À poster »`);
      onSaved(r.data.project);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); setSaving(false); }
  };

  if (!posts) {
    return (
      <Modal title="Projet Postage" subtitle="Que des posts — l'IA lit ton doc" onClose={onClose}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Contenu du doc *" hint="Colle tout le doc : titres, descriptions, liens Drive, dates. L'IA crée 1 post par vidéo.">
            <textarea data-testid="postage-doc-input" value={docText} onChange={(e) => setDocText(e.target.value)} rows={12} placeholder="Colle ici le Google Doc des posts…" style={{ ...inp, resize: 'vertical' }} />
          </Field>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onBack} className="asa-btn asa-btn-ghost">Retour</button>
            <button type="button" data-testid="postage-analyse-btn" onClick={analyse} disabled={loading} className="asa-btn asa-btn-primary" style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              {loading ? <Loader2 size={15} className="spin" /> : <Sparkles size={15} />}{loading ? 'Lecture du doc…' : 'Analyser avec l\'IA'}
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Projet Postage · Vérifie" subtitle={`${posts.length} post(s) — tout part direct « À poster »`} onClose={onClose} maxWidth={760}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Nom du projet *"><input data-testid="postage-name" value={name} onChange={(e) => setName(e.target.value)} style={inp} /></Field>
          <Field label="Client"><input data-testid="postage-client" value={client} onChange={(e) => setClient(e.target.value)} style={inp} /></Field>
        </div>
        <Field label="Posteur *">
          <select data-testid="postage-poster" value={posterId} onChange={(e) => setPosterId(e.target.value)} style={{ ...inp, colorScheme: 'dark' }}>
            <option value="">— Choisir —</option>
            {posters.map((u) => <option key={u.id} value={u.id}>{u.name}{u.job_role === 'posteur' ? ' · Posteur' : ''}</option>)}
          </select>
        </Field>
        <Field label="Dates (optionnel)" hint="Les dates trouvées dans le doc sont gardées. Sinon applique un rythme, ou laisse vide → demain.">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input data-testid="postage-start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ ...inp, colorScheme: 'dark' }} />
            <RhythmInput rhythm={rhythm} setRhythm={setRhythm} startDate={startDate} videoCount={posts.length} />
            <button type="button" data-testid="postage-apply-rhythm" onClick={applyRhythm} disabled={!rhythm} className="asa-btn asa-btn-ghost" style={{ alignSelf: 'flex-start' }}>Appliquer le rythme à tous les posts</button>
          </div>
        </Field>
        <div className="asa-eyebrow">Posts</div>
        {posts.map((p, i) => (
          <div key={i} data-testid={`postage-post-${i}`} style={{ padding: 12, borderRadius: 12, background: 'var(--sf2)', border: '1.5px solid var(--bd)', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Smartphone size={14} color="#5BBEFC" />
              <input data-testid={`postage-post-title-${i}`} value={p.title} onChange={(e) => upd(i, 'title', e.target.value)} placeholder="Titre de la vidéo" style={{ ...inp, fontWeight: 700, fontSize: 13.5 }} />
              <input data-testid={`postage-post-date-${i}`} type="date" value={p.date || ''} onChange={(e) => upd(i, 'date', e.target.value || null)} style={{ ...inp, width: 150, colorScheme: 'dark' }} />
              <button type="button" data-testid={`postage-post-remove-${i}`} onClick={() => setPosts((ps) => ps.filter((_, j) => j !== i))} style={{ background: 'transparent', border: 'none', color: '#FF7B7B', cursor: 'pointer' }}><Trash2 size={14} /></button>
            </div>
            <textarea data-testid={`postage-post-desc-${i}`} value={p.description || ''} onChange={(e) => upd(i, 'description', e.target.value)} rows={2} placeholder="Description à coller" style={{ ...inp, resize: 'vertical', color: 'var(--t2)' }} />
            <input data-testid={`postage-post-drive-${i}`} value={p.drive_link || ''} onChange={(e) => upd(i, 'drive_link', e.target.value || null)} placeholder="Lien Drive de la vidéo" style={{ ...inp, color: '#5BBEFC', fontSize: 12 }} />
          </div>
        ))}
        <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
          <button type="button" onClick={() => setPosts(null)} className="asa-btn asa-btn-ghost">Retour au doc</button>
          <button type="button" data-testid="postage-submit" onClick={submit} disabled={saving} className="asa-btn asa-btn-primary" style={{ flex: 1 }}>{saving ? 'Création…' : `Créer le projet · ${posts.length} post(s)`}</button>
        </div>
      </div>
    </Modal>
  );
}
